import { forwardRef, useMemo, useRef, useState, useLayoutEffect } from 'react';
import SimpleBar from 'simplebar-react';
import { alpha, styled } from '@mui/material/styles';
import 'simplebar-react/dist/simplebar.min.css';

const StyledSimpleBar = styled(SimpleBar)(({ theme }) => ({
    '& .simplebar-track.simplebar-vertical': {
        width: '1px',
        background: alpha(theme.palette.divider, 0.5),
        overflow: 'visible !important',
        opacity: 1,
        marginRight: 4,
        bottom: 'var(--csb-bottom-offset, 0px)',
    },
    '& .simplebar-scrollbar::before': {
        background: alpha(theme.palette.text.secondary, 0.8),
        width: '3px',
        left: '-1px',
        borderRadius: '0px',
        top: '0px',
        bottom: '0px',
        opacity: 1,
    },
    '@media (hover: none) and (pointer: coarse)': {
        '& .simplebar-track.simplebar-vertical': {
            display: 'none',
        },
        '& .simplebar-scrollbar::before': {
            display: 'none',
        },
    }
}));

const mergeRefs = (...refs) => (node) => {
    refs.forEach((ref) => {
        if (!ref) return;
        if (typeof ref === 'function') ref(node);
        else ref.current = node;
    });
};

const CustomScrollBar = forwardRef(function CustomScrollBar(
    { style, children, paddingWhenScroll = 4, bottomOffset = '0px', ...rest },
    ref
) {
    const localRef = useRef(null);
    const [wrapperEl, setWrapperEl] = useState(null);
    const [hasVScroll, setHasVScroll] = useState(false);
    const isTouchDevice = useMemo(() => {
        try {
            if (typeof window !== 'undefined' && window.matchMedia) {
                if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) return true;
            }
            if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) return true;
        } catch {}
        return false;
    }, []);

    useLayoutEffect(() => {
        const wrapper = wrapperEl;
        if (!wrapper) return;

        const update = () => {
            try {
                setHasVScroll(wrapper.scrollHeight > wrapper.clientHeight);
            } catch {
                // ignore
            }
        };

        update();

        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (ro) {
            ro.observe(wrapper);
        }

        wrapper.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);

        return () => {
            try { ro && ro.disconnect && ro.disconnect(); } catch {}
            wrapper.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [wrapperEl]);

    const handleRef = useMemo(() => (node) => {
        mergeRefs(ref, localRef)(node);
        if (!node) {
            setWrapperEl(null);
            return;
        }
        let wrapper = null;
        try {
            if (node instanceof Element) {
                wrapper = node.querySelector?.('.simplebar-content-wrapper') || null;
            } else if (node && typeof node.getScrollElement === 'function') {
                wrapper = node.getScrollElement();
            } else if (node && node.el instanceof Element) {
                wrapper = node.el.querySelector?.('.simplebar-content-wrapper') || null;
            }
        } catch {
            wrapper = null;
        }
        setWrapperEl(wrapper);
        if (wrapper) {
            try {
                setHasVScroll(wrapper.scrollHeight > wrapper.clientHeight);
            } catch {}
        }
    }, [ref]);

    return (
        <StyledSimpleBar
            ref={handleRef}
            style={{
                '--csb-bottom-offset': bottomOffset,
                paddingRight: (!isTouchDevice && hasVScroll) ? paddingWhenScroll : 0,
                ...style
            }}
            {...rest}
        >
            {children}
        </StyledSimpleBar>
    );
});

export default CustomScrollBar;
